<template>
  <div
    ref="triggerRef"
    class="ecx-tooltip-trigger inline-flex"
    @mouseenter="showTooltip"
    @mouseleave="hideTooltip"
    @focusin="showTooltip"
    @focusout="hideTooltip"
  >
    <slot />

    <Teleport to="body">
      <Transition name="tooltip">
        <div
          v-if="isVisible && (content || $slots.content)"
          ref="tooltipRef"
          class="ecx-tooltip"
          :class="tooltipClasses"
          :style="tooltipStyle"
          role="tooltip"
        >
          <slot name="content">
            <span v-if="html" v-html="content" />
            <template v-else>{{ content }}</template>
          </slot>
          <div v-if="arrow" class="ecx-tooltip-arrow" :class="arrowClasses" />
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, nextTick, type CSSProperties } from 'vue'

export type TooltipPlacement = 'top' | 'bottom' | 'left' | 'right'
export type TooltipVariant = 'dark' | 'light'

export interface TooltipProps {
  /** Conteúdo do tooltip */
  content?: string
  /** Posição do tooltip */
  placement?: TooltipPlacement
  /** Variante de cor */
  variant?: TooltipVariant
  /** Mostrar seta */
  arrow?: boolean
  /** Delay para mostrar (ms) */
  delay?: number
  /** Desabilitar tooltip */
  disabled?: boolean
  /** Renderizar conteúdo como HTML */
  html?: boolean
  /** Largura máxima do tooltip (ex: "288px", "18rem") */
  maxWidth?: string
  /** Alinhamento horizontal (placement top/bottom) ou vertical (placement left/right) */
  align?: 'start' | 'center' | 'end'
}

const props = withDefaults(defineProps<TooltipProps>(), {
  placement: 'top',
  variant: 'dark',
  arrow: true,
  delay: 0,
  disabled: false,
  html: false,
  maxWidth: undefined,
  align: 'center',
})

const triggerRef = ref<HTMLElement | null>(null)
const tooltipRef = ref<HTMLElement | null>(null)
const isVisible = ref(false)
const position = ref({ top: 0, left: 0 })
let timeoutId: ReturnType<typeof setTimeout> | null = null

const tooltipClasses = computed(() => {
  return `ecx-tooltip--${props.variant}`
})

const arrowClasses = computed(() => {
  return `ecx-tooltip-arrow--${props.placement} ecx-tooltip-arrow--${props.variant}`
})

const tooltipStyle = computed<CSSProperties>(() => {
  const style: CSSProperties = {
    position: 'fixed',
    top: `${position.value.top}px`,
    left: `${position.value.left}px`,
    zIndex: 9999,
  }
  if (props.maxWidth) {
    style.maxWidth = props.maxWidth
  }
  return style
})

const calculatePosition = async () => {
  await nextTick()
  if (!triggerRef.value || !tooltipRef.value) return

  const triggerRect = triggerRef.value.getBoundingClientRect()
  const tooltipRect = tooltipRef.value.getBoundingClientRect()
  const gap = 8

  let top = 0
  let left = 0

  const alignLeft = () => {
    switch (props.align) {
      case 'start':
        return triggerRect.left
      case 'end':
        return triggerRect.right - tooltipRect.width
      default:
        return triggerRect.left + (triggerRect.width - tooltipRect.width) / 2
    }
  }
  const alignTop = () => {
    switch (props.align) {
      case 'start':
        return triggerRect.top
      case 'end':
        return triggerRect.bottom - tooltipRect.height
      default:
        return triggerRect.top + (triggerRect.height - tooltipRect.height) / 2
    }
  }

  switch (props.placement) {
    case 'top':
      top = triggerRect.top - tooltipRect.height - gap
      left = alignLeft()
      break
    case 'bottom':
      top = triggerRect.bottom + gap
      left = alignLeft()
      break
    case 'left':
      top = alignTop()
      left = triggerRect.left - tooltipRect.width - gap
      break
    case 'right':
      top = alignTop()
      left = triggerRect.right + gap
      break
  }

  // Keep within viewport
  const padding = 8
  left = Math.max(padding, Math.min(left, window.innerWidth - tooltipRect.width - padding))
  top = Math.max(padding, Math.min(top, window.innerHeight - tooltipRect.height - padding))

  position.value = { top, left }
}

const showTooltip = () => {
  if (props.disabled) return
  
  if (props.delay > 0) {
    timeoutId = setTimeout(() => {
      isVisible.value = true
      calculatePosition()
    }, props.delay)
  } else {
    isVisible.value = true
    calculatePosition()
  }
}

const hideTooltip = () => {
  if (timeoutId) {
    clearTimeout(timeoutId)
    timeoutId = null
  }
  isVisible.value = false
}

onMounted(() => {
  window.addEventListener('scroll', calculatePosition, true)
  window.addEventListener('resize', calculatePosition)
})

onUnmounted(() => {
  window.removeEventListener('scroll', calculatePosition, true)
  window.removeEventListener('resize', calculatePosition)
  if (timeoutId) clearTimeout(timeoutId)
})
</script>

<style>
.ecx-tooltip {
  padding: 6px 10px;
  border-radius: 6px;
  font-size: 12px;
  max-width: 250px;
  word-wrap: break-word;
}

/* Variantes */
.ecx-tooltip--dark {
  background-color: #3D4168;
  color: #ffffff;
}

.ecx-tooltip--light {
  background-color: #ffffff;
  color: #1E2139;
  border: 1px solid #E5E7EB;
  box-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1);
}

/* Arrow base */
.ecx-tooltip-arrow {
  position: absolute;
  width: 0;
  height: 0;
}

/* Arrow positions */
.ecx-tooltip-arrow--top {
  bottom: 0;
  left: 50%;
  transform: translateX(-50%) translateY(100%);
  border-left: 8px solid transparent;
  border-right: 8px solid transparent;
  border-bottom: 0;
  border-top-width: 8px;
  border-top-style: solid;
}

.ecx-tooltip-arrow--bottom {
  top: 0;
  left: 50%;
  transform: translateX(-50%) translateY(-100%);
  border-left: 8px solid transparent;
  border-right: 8px solid transparent;
  border-top: 0;
  border-bottom-width: 8px;
  border-bottom-style: solid;
}

.ecx-tooltip-arrow--left {
  right: 0;
  top: 50%;
  transform: translateY(-50%) translateX(100%);
  border-top: 8px solid transparent;
  border-bottom: 8px solid transparent;
  border-right: 0;
  border-left-width: 8px;
  border-left-style: solid;
}

.ecx-tooltip-arrow--right {
  left: 0;
  top: 50%;
  transform: translateY(-50%) translateX(-100%);
  border-top: 8px solid transparent;
  border-bottom: 8px solid transparent;
  border-left: 0;
  border-right-width: 8px;
  border-right-style: solid;
}

/* Arrow colors */
.ecx-tooltip-arrow--dark.ecx-tooltip-arrow--top {
  border-top-color: #3D4168;
}
.ecx-tooltip-arrow--dark.ecx-tooltip-arrow--bottom {
  border-bottom-color: #3D4168;
}
.ecx-tooltip-arrow--dark.ecx-tooltip-arrow--left {
  border-left-color: #3D4168;
}
.ecx-tooltip-arrow--dark.ecx-tooltip-arrow--right {
  border-right-color: #3D4168;
}

.ecx-tooltip-arrow--light.ecx-tooltip-arrow--top {
  border-top-color: #ffffff;
}
.ecx-tooltip-arrow--light.ecx-tooltip-arrow--bottom {
  border-bottom-color: #ffffff;
}
.ecx-tooltip-arrow--light.ecx-tooltip-arrow--left {
  border-left-color: #ffffff;
}
.ecx-tooltip-arrow--light.ecx-tooltip-arrow--right {
  border-right-color: #ffffff;
}

/* Transitions */
.tooltip-enter-active,
.tooltip-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}

.tooltip-enter-from,
.tooltip-leave-to {
  opacity: 0;
  transform: scale(0.95);
}
</style>

